<template>
  <div class="h-auto flex flex-col modal-wrapper pb-4">
    <!-- Custom Header with Blue Background -->
    <div class="custom-modal-header">
      <div class="header-content">
        <h2 class="header-title">
          {{ $t('CAMPAIGN.SMART_AI_EMAIL.ADD.TITLE') }}
        </h2>
        <button
          type="button"
          class="close-button"
          @click="onClose"
          aria-label="Close"
        >
          <fluent-icon icon="dismiss" size="20" />
        </button>
      </div>
      <p class="header-description">
        {{ $t('CAMPAIGN.SMART_AI_EMAIL.ADD.DESC') }}
      </p>
    </div>

    <form class="flex flex-col w-full form-container" @submit.prevent="addCampaign">
      <div class="w-full form-content">
        <!-- Campaign Title -->
        <woot-input
          v-model="title"
          :label="$t('CAMPAIGN.ADD.FORM.TITLE.LABEL')"
          type="text"
          :class="{ error: $v.title.$error }"
          :error="$v.title.$error ? $t('CAMPAIGN.ADD.FORM.TITLE.ERROR') : ''"
          :placeholder="$t('CAMPAIGN.ADD.FORM.TITLE.PLACEHOLDER')"
          @blur="$v.title.$touch"
        />

        <!-- Campaign Description (Optional) -->
        <div class="form-group">
          <label>
            {{ $t('CAMPAIGN.ADD.FORM.DESCRIPTION.LABEL') }}
            <span class="optional-label">{{ $t('CAMPAIGN.ADD.FORM.DESCRIPTION.OPTIONAL') }}</span>
          </label>
          <textarea
            v-model="description"
            :placeholder="$t('CAMPAIGN.ADD.FORM.DESCRIPTION.PLACEHOLDER')"
            class="description-textarea"
            rows="3"
          />
        </div>

        <!-- Email Inbox Selection -->
        <div class="form-group" :class="{ error: $v.selectedInbox.$error }">
          <label>
            {{ $t('CAMPAIGN.SMART_AI_EMAIL.ADD.FORM.INBOX.LABEL') }}
          </label>
          <multiselect
            v-model="selectedInboxObject"
            :options="emailInboxes"
            track-by="id"
            label="name"
            :placeholder="$t('CAMPAIGN.SMART_AI_EMAIL.ADD.FORM.INBOX.PLACEHOLDER')"
            :allow-empty="false"
            :show-labels="false"
            :close-on-select="true"
            @input="onChangeInbox"
          />
          <span v-if="$v.selectedInbox.$error" class="message">
            {{ $t('CAMPAIGN.SMART_AI_EMAIL.ADD.FORM.INBOX.ERROR') }}
          </span>
        </div>

        <div
          v-if="showEmailOutboundProvider"
          class="form-group campaign-smart-ai-outbound-provider mb-6"
        >
          <label>{{ $t('CAMPAIGN.ADD.FORM.OUTBOUND_PROVIDER.LABEL') }}</label>
          <multiselect
            :value="selectedEmailOutboundOption"
            :options="emailOutboundProviderOptions"
            label="label"
            track-by="value"
            :allow-empty="false"
            :searchable="false"
            :show-labels="false"
            :close-on-select="true"
            @input="onEmailOutboundProviderInput"
          />
        </div>

        <!-- Custom Filter (Audience) Selection -->
        <div class="form-group" :class="{ error: $v.selectedFilter.$error }">
          <label>
            {{ $t('CAMPAIGN.SMART_AI_EMAIL.ADD.FORM.FILTER.LABEL') }}
          </label>
          <multiselect
            v-model="selectedFilterObject"
            :options="contactFilters"
            track-by="id"
            label="name"
            :placeholder="$t('CAMPAIGN.SMART_AI_EMAIL.ADD.FORM.FILTER.PLACEHOLDER')"
            :allow-empty="false"
            :show-labels="false"
            :close-on-select="true"
            @input="onChangeFilter"
          />
          <span v-if="$v.selectedFilter.$error" class="message">
            {{ $t('CAMPAIGN.SMART_AI_EMAIL.ADD.FORM.FILTER.ERROR') }}
          </span>
        </div>

        <!-- Macro Selection -->
        <div class="form-group" :class="{ error: $v.selectedMacro.$error }">
          <label class="label-with-info">
            {{ $t('CAMPAIGN.SMART_AI_EMAIL.ADD.FORM.MACRO.LABEL') }}
            <button
              v-tooltip.top="$t('CAMPAIGN.SMART_AI_EMAIL.ADD.FORM.MACRO.INFO')"
              type="button"
              class="info-button"
              @click.prevent
            >
              <fluent-icon icon="info" size="16" />
            </button>
          </label>
          <multiselect
            v-model="selectedMacroObject"
            :options="webhookMacros"
            track-by="id"
            label="name"
            :placeholder="$t('CAMPAIGN.SMART_AI_EMAIL.ADD.FORM.MACRO.PLACEHOLDER')"
            :allow-empty="false"
            :show-labels="false"
            :close-on-select="true"
            @input="onChangeMacro"
          />
          <span v-if="$v.selectedMacro.$error" class="message">
            {{ $t('CAMPAIGN.SMART_AI_EMAIL.ADD.FORM.MACRO.ERROR') }}
          </span>
        </div>

        <!-- Random Delay and Emails Per Contact in one line -->
        <div class="flex gap-4 mb-0">
          <!-- Random Delay -->
          <div class="form-group flex-1 mb-0">
            <label class="label-with-info">
              {{ $t('CAMPAIGN.SMART_AI_EMAIL.ADD.FORM.RANDOM_DELAY.LABEL') }}
              <button
                v-tooltip.top="$t('CAMPAIGN.SMART_AI_EMAIL.ADD.FORM.RANDOM_DELAY.INFO')"
                type="button"
                class="info-button"
                @click.prevent
              >
                <fluent-icon icon="info" size="16" />
              </button>
            </label>
            <multiselect
              v-model="randomDelayMinutes"
              :options="randomDelayOptions"
              :allow-empty="false"
              :searchable="false"
              :show-labels="false"
              :close-on-select="true"
              :append-to-body="false"
            />
          </div>

          <!-- Emails Per Contact -->
          <div class="form-group flex-1 mb-0">
            <label>
              {{ $t('CAMPAIGN.SMART_AI_EMAIL.ADD.FORM.EMAILS_PER_CONTACT.LABEL') }}
            </label>
            <multiselect
              v-model="emailsPerContact"
              :options="emailsPerContactOptions"
              :allow-empty="false"
              :searchable="false"
              :show-labels="false"
              :close-on-select="true"
              :append-to-body="false"
            />
          </div>
        </div>

        <!-- Scheduled Times for Each Email -->
        <div class="form-group">
          <label style="font-size: 1rem;">
            {{ $t('CAMPAIGN.SMART_AI_EMAIL.ADD.FORM.SCHEDULED_TIMES.LABEL') }}
          </label>
          <div 
            v-for="rowIndex in Math.ceil(emailsPerContact / 2)" 
            :key="rowIndex" 
            class="email-time-row"
          >
            <div class="flex gap-4">
              <!-- First email in the row -->
              <div class="flex-1">
                <label class="text-sm text-slate-600 dark:text-slate-400 mb-2 block">
                  {{ $t('CAMPAIGN.SMART_AI_EMAIL.ADD.FORM.SCHEDULED_TIMES.EMAIL_LABEL', { num: (rowIndex - 1) * 2 + 1 }) }}
                </label>
                <woot-date-time-picker
                  :value="scheduledTimes[(rowIndex - 1) * 2]"
                  :confirm-text="$t('CAMPAIGN.ADD.FORM.SCHEDULED_AT.CONFIRM')"
                  :placeholder="$t('CAMPAIGN.ADD.FORM.SCHEDULED_AT.PLACEHOLDER')"
                  @change="(val) => onScheduledTimeChange((rowIndex - 1) * 2, val)"
                />
              </div>
              
              <!-- Second email in the row (if exists) -->
              <div 
                v-if="(rowIndex - 1) * 2 + 1 < emailsPerContact"
                class="flex-1"
              >
                <label class="text-sm text-slate-600 dark:text-slate-400 mb-2 block">
                  {{ $t('CAMPAIGN.SMART_AI_EMAIL.ADD.FORM.SCHEDULED_TIMES.EMAIL_LABEL', { num: (rowIndex - 1) * 2 + 2 }) }}
                </label>
                <woot-date-time-picker
                  :value="scheduledTimes[(rowIndex - 1) * 2 + 1]"
                  :confirm-text="$t('CAMPAIGN.ADD.FORM.SCHEDULED_AT.CONFIRM')"
                  :placeholder="$t('CAMPAIGN.ADD.FORM.SCHEDULED_AT.PLACEHOLDER')"
                  @change="(val) => onScheduledTimeChange((rowIndex - 1) * 2 + 1, val)"
                />
              </div>
              
              <!-- Empty spacer to maintain alignment when odd number -->
              <div 
                v-else
                class="flex-1"
              ></div>
            </div>
          </div>
        </div>
      </div>

      <div class="flex flex-row justify-end gap-2 py-2 px-0 w-full">
        <woot-button
          :is-loading="uiFlags.isCreating"
          :is-disabled="isFormInvalid"
        >
          {{ $t('CAMPAIGN.ADD.CREATE_BUTTON_TEXT') }}
        </woot-button>
        <woot-button variant="clear" @click.prevent="onClose">
          {{ $t('CAMPAIGN.ADD.CANCEL_BUTTON_TEXT') }}
        </woot-button>
      </div>
    </form>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { required } from 'vuelidate/lib/validators';
import alertMixin from 'shared/mixins/alertMixin';
import WootDateTimePicker from 'dashboard/components/ui/DateTimePicker.vue';
import CustomViewsAPI from 'dashboard/api/customViews';

export default {
  components: {
    WootDateTimePicker,
  },
  mixins: [alertMixin],
  data() {
    return {
      title: '',
      description: '',
      selectedInbox: null,
      selectedInboxObject: null,
      selectedFilter: null,
      selectedFilterObject: null,
      selectedMacro: null,
      selectedMacroObject: null,
      emailsPerContact: 1,
      randomDelayMinutes: 0,
      scheduledTimes: ['', '', '', '', ''],
      contactFilters: [],
      emailsPerContactOptions: [1, 2, 3, 4, 5],
      randomDelayOptions: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
      emailOutboundProvider: 'default',
      emailOutboundProviderOptions: [
        { value: 'default', label: 'Default (SMTP / Microsoft)' },
        { value: 'sendgrid', label: 'SendGrid' },
      ],
    };
  },
  validations: {
    title: { required },
    selectedInbox: { required },
    selectedFilter: { required },
    selectedMacro: { required },
  },
  computed: {
    ...mapGetters({
      uiFlags: 'campaigns/getUIFlags',
      allInboxes: 'inboxes/getInboxes',
      allMacros: 'macros/getMacros',
    }),
    emailInboxes() {
      return this.allInboxes.filter(inbox => inbox.channel_type === 'Channel::Email');
    },
    webhookMacros() {
      // Filter macros that have send_api_webhook action
      return this.allMacros.filter(macro => {
        if (!macro.actions) return false;
        return macro.actions.some(action => action.action_name === 'send_api_webhook');
      });
    },
    isFormInvalid() {
      if (!this.title || !this.selectedInbox || !this.selectedFilter || !this.selectedMacro) {
        return true;
      }
      // Validate scheduled times
      for (let i = 0; i < this.emailsPerContact; i++) {
        if (!this.scheduledTimes[i]) return true;
      }
      return false;
    },
    showEmailOutboundProvider() {
      if (!this.selectedInboxObject) return false;
      const inbox = this.selectedInboxObject;
      const hasDefault =
        inbox.smtp_enabled ||
        (inbox.provider === 'microsoft' && inbox.imap_enabled);
      const hasSendgrid =
        inbox.sendgrid_outbound_configured === true ||
        !!(inbox.provider_config?.sendgrid?.enabled && inbox.provider_config?.sendgrid?.api_key);
      return hasDefault && hasSendgrid;
    },
    selectedEmailOutboundOption() {
      return (
        this.emailOutboundProviderOptions.find(
          o => o.value === this.emailOutboundProvider
        ) || this.emailOutboundProviderOptions[0]
      );
    },
  },
  mounted() {
    this.fetchContactFilters();
    this.$store.dispatch('macros/get');
    this.$store.dispatch('inboxes/get');
  },
  methods: {
    async fetchContactFilters() {
      try {
        const { data } = await CustomViewsAPI.getCustomViewsByFilterType('contact');
        this.contactFilters = Array.isArray(data) ? data : [];
      } catch (e) {
        console.error('Error fetching contact filters:', e);
      }
    },
    onChangeInbox() {
      this.selectedInbox = this.selectedInboxObject?.id || null;
      this.emailOutboundProvider = 'default';
    },
    onEmailOutboundProviderInput(option) {
      this.emailOutboundProvider =
        option && option.value != null ? option.value : 'default';
    },
    onChangeFilter() {
      this.selectedFilter = this.selectedFilterObject?.id || null;
    },
    onChangeMacro() {
      this.selectedMacro = this.selectedMacroObject?.id || null;
    },
    onScheduledTimeChange(index, value) {
      this.$set(this.scheduledTimes, index, value);
    },
    onClose() {
      this.$emit('on-close');
    },
    getCampaignDetails() {
      // Only include scheduled times up to emailsPerContact
      const activeScheduledTimes = this.scheduledTimes.slice(0, this.emailsPerContact);

      const triggerRules = {
        emails_per_contact: this.emailsPerContact,
        random_delay_minutes: this.randomDelayMinutes,
        scheduled_times: activeScheduledTimes,
      };
      if (this.showEmailOutboundProvider) {
        triggerRules.outbound_provider =
          this.emailOutboundProvider === 'sendgrid' ? 'sendgrid' : 'default';
      }

      const payload = {
        title: this.title,
        description: this.description || '',
        message: 'Smart AI Email Campaign', // Placeholder message
        inbox_id: this.selectedInbox,
        macro_id: this.selectedMacro,
        custom_filter_id: this.selectedFilter,
        campaign_type: 'smart_ai_email_campaign',
        trigger_rules: triggerRules,
      };
      if (this.showEmailOutboundProvider && this.emailOutboundProvider === 'sendgrid') {
        payload.outbound_email_provider = 'sendgrid';
      }
      return payload;
    },
    async addCampaign() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }
      try {
        const campaignDetails = this.getCampaignDetails();
        await this.$store.dispatch('campaigns/create', campaignDetails);
        this.showAlert(
          this.$t('CAMPAIGN.SMART_AI_EMAIL.ADD.API.SUCCESS_MESSAGE')
        );
        this.$emit('campaign-created');
        this.onClose();
      } catch (error) {
        const errorMessage =
          error?.response?.data?.message ||
          this.$t('CAMPAIGN.SMART_AI_EMAIL.ADD.API.ERROR_MESSAGE');
        this.showAlert(errorMessage);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.modal-wrapper {
  max-height: 80vh;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border-radius: 12px;
}

.dark .modal-wrapper {
  background-color: rgb(30 41 59);
}

/* Custom Header with Blue Background */
.custom-modal-header {
  background: #40518a;
  padding: 1.5rem 1.5rem;
  padding-bottom: 1rem;
  border-radius: 12px 12px 0 0;
  position: relative;

  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 0.5rem;
  }

  .header-title {
    color: white;
    font-size: 1.25rem;
    font-weight: 600;
    margin: 0;
    line-height: 1.4;
    flex: 1;
    padding-right: 1rem;
  }

  .close-button {
    position: absolute;
    right: 8px;
    background: rgba(255, 255, 255, 0.2);
    border: none;
    border-radius: 6px;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: white;
    transition: all 0.2s ease;
    flex-shrink: 0;

    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }

    &:active {
      transform: scale(0.95);
    }
  }

  .header-description {
    color: rgba(255, 255, 255, 0.95);
    font-size: 0.875rem;
    margin: 0;
    line-height: 1.5;
    max-width: 90%;
  }
}

.form-container {
  overflow-y: auto;
  overflow-x: visible;
  flex: 1;
  padding: 0 2rem;
  position: relative;
  z-index: 1;
}

.form-content {
  padding-top: 1.5rem;
  padding-bottom: 1rem;
  position: relative;
}

.campaign-smart-ai-outbound-provider {
  ::v-deep .multiselect__content-wrapper {
    @apply z-[100];
  }

  ::v-deep .multiselect {
    @apply rounded-md mb-0;
  }

  ::v-deep .multiselect__tags {
    @apply rounded-md;
  }
}

.form-group {
  @apply mb-4;

  label {
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--s-700);
    margin-bottom: 0.5rem;

    .optional-label {
      font-weight: 400;
      color: var(--s-500);
      font-size: 0.8125rem;
      margin-left: 0.25rem;
    }
  }

  .label-with-info {
    display: flex;
    align-items: center;
    gap: 0.375rem;
  }

  .info-button {
    background: transparent;
    border: none;
    padding: 0;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: var(--w-500);
    transition: all 0.2s ease;
    border-radius: 50%;
    width: 20px;
    height: 20px;

    &:hover {
      color: var(--w-600);
      background: rgba(31, 147, 255, 0.1);
    }

    &:active {
      transform: scale(0.95);
    }

    &:focus {
      outline: none;
      box-shadow: 0 0 0 2px rgba(31, 147, 255, 0.2);
    }
  }
}

.description-textarea {
  width: 100%;
  padding: 0.625rem 0.75rem;
  border: 1px solid var(--s-200);
  border-radius: 8px;
  font-size: 0.875rem;
  font-family: inherit;
  resize: vertical;
  transition: all 0.2s ease;
  background-color: white;

  &:focus {
    outline: none;
    border-color: var(--w-500);
    box-shadow: 0 0 0 3px rgba(31, 147, 255, 0.1);
  }

  &::placeholder {
    color: var(--s-400);
  }
}

.dark .description-textarea {
  background-color: var(--s-800);
  border-color: var(--s-700);
  color: var(--s-100);

  &:focus {
    border-color: var(--w-500);
  }
}

.email-time-row {
  margin-bottom: 1rem;
}

.email-time-row:last-child {
  margin-bottom: 0;
}

::v-deep .multiselect__placeholder {
  margin-left: -3px;
}

::v-deep .multiselect__input {
  background-color: transparent !important;
}

::v-deep .multiselect__content-wrapper {
  position: absolute;
  z-index: 50;
  max-height: 200px;
}

::v-deep .multiselect {
  position: relative;
}

/* Add border radius to modal container */
::v-deep .modal-container {
  border-radius: 12px;
  overflow: hidden;
}

/* Fix DateTimePicker z-index to stay within modal */
::v-deep .mx-datepicker-popup {
  z-index: 100 !important;
  position: absolute !important;
}

::v-deep .mx-datepicker {
  position: relative;
}

/* Ensure modal has proper stacking context */
.modal-wrapper {
  position: relative;
  z-index: 1;
}

/* Override date picker default z-index */
::v-deep .date-picker {
  position: relative;
  z-index: 1;
}

/* Tooltip styling */
::v-deep .tooltip {
  z-index: 10000 !important;
}

::v-deep .tooltip-inner {
  background: var(--s-900) !important;
  color: white !important;
  padding: 0.75rem 1rem !important;
  border-radius: 8px !important;
  font-size: 0.8125rem !important;
  line-height: 1.5 !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
  max-width: 320px !important;
}

::v-deep .tooltip-arrow {
  border-color: var(--s-900) !important;
}

.dark ::v-deep .tooltip-inner {
  background: var(--s-800) !important;
  border: 1px solid var(--s-700) !important;
}
</style>

